import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  HeartPulse, ShieldCheck, BadgeCheck, Wallet, Sparkles, Globe2, Mail,
  ClipboardCheck, Search, FileCheck2, RefreshCw, ArrowRight,
} from "lucide-react";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export default function About() {
  useEffect(() => {
    document.title = "About SarvaSeva | Global Medical Tourism to India";
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) { tag = document.createElement("meta"); tag.name = "description"; document.head.appendChild(tag); }
    tag.setAttribute("content", "SarvaSeva connects global patients with India's accredited (JCI/NABH) hospitals — transparent pricing, escrow-protected payments and AI-powered hospital matching.");
  }, []);

  const values = [
    { icon: Wallet, title: "Transparent Pricing", desc: "Every package lists an all-inclusive price up front — no hidden fees, no surprises. Compare hospitals side by side before you decide." },
    { icon: ShieldCheck, title: "Escrow Protection", desc: "Your payment is held safely in escrow and released to the hospital only as treatment milestones are met, so your money stays protected." },
    { icon: Sparkles, title: "AI-Powered Matching", desc: "Our matchmaker reads your needs and instantly recommends the best-fit accredited hospitals across specialty, outcomes and budget." },
  ];

  const steps = [
    { icon: Search, title: "Discovery", desc: "We identify hospitals with strong clinical outcomes and international patient experience across India." },
    { icon: FileCheck2, title: "Accreditation Check", desc: "We verify current JCI and NABH accreditation directly against official records before a hospital is listed." },
    { icon: ClipboardCheck, title: "Quality Review", desc: "Facilities, specialties, success rates and patient safety practices are reviewed and documented on each profile." },
    { icon: RefreshCw, title: "Ongoing Monitoring", desc: "Accreditation status is re-checked periodically — listings are updated or paused if standards lapse." },
  ];

  return (
    <div className="bg-[#F7F9FC] min-h-screen" data-testid="about-page">
      {/* Hero */}
      <section className="tricolor-top relative overflow-hidden mv-gradient-hero text-white">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <motion.div {...fade} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-sm font-medium">
              <Globe2 size={16} /> Global patients · Indian excellence
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight max-w-3xl">
              Making world-class care in India accessible, transparent and safe
            </h1>
            <p className="mt-5 text-white/90 text-lg max-w-2xl">
              SarvaSeva connects patients around the world with India's most trusted, accredited hospitals — guided by clear pricing, protected payments and intelligent matching.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20 space-y-20">
        {/* Mission */}
        <motion.section {...fade} transition={{ duration: 0.5 }} data-testid="about-mission">
          <span className="section-overline mb-3" />
          <h2 className="font-heading text-3xl font-extrabold text-[#101828]">Our Mission</h2>
          <p className="mt-4 text-[#475467] text-lg leading-relaxed max-w-3xl">
            Every year, thousands of patients travel abroad for treatment they cannot access or afford at home. Our mission is to
            connect global patients with India's best hospitals — pairing internationally accredited clinical excellence with
            honest pricing and end-to-end support, so that a life-changing procedure is never out of reach.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: HeartPulse, stat: "117+", label: "Accredited partner hospitals" },
              { icon: Globe2, stat: "23", label: "Cities across India" },
              { icon: BadgeCheck, stat: "JCI · NABH", label: "Verified accreditation" },
            ].map((s) => (
              <div key={s.label} className="card-modern p-6 flex items-center gap-4" data-testid="about-stat">
                <span className="h-12 w-12 rounded-2xl grid place-items-center bg-[#0D5C75]/10 text-[#0D5C75]"><s.icon size={24} /></span>
                <div>
                  <div className="font-heading font-extrabold text-2xl text-[#101828] leading-none">{s.stat}</div>
                  <div className="text-sm text-muted-foreground mt-1">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Accreditation process */}
        <motion.section {...fade} transition={{ duration: 0.5 }} data-testid="about-accreditation">
          <span className="section-overline mb-3" />
          <h2 className="font-heading text-3xl font-extrabold text-[#101828]">How We Verify Accreditation</h2>
          <p className="mt-4 text-[#475467] text-lg leading-relaxed max-w-3xl">
            We only list hospitals that meet globally recognised quality standards. Accreditation by the{" "}
            <span className="font-semibold text-[#101828]">Joint Commission International (JCI)</span> and India's{" "}
            <span className="font-semibold text-[#101828]">National Accreditation Board for Hospitals & Healthcare Providers (NABH)</span>{" "}
            signals rigorous patient-safety, infection-control and clinical-outcome standards.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {steps.map((s, i) => (
              <div key={s.title} className="card-modern tricolor-top p-6 h-full" data-testid={`about-step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <span className="h-11 w-11 rounded-2xl grid place-items-center bg-[#2A9D8F]/12 text-[#0D5C75]"><s.icon size={22} /></span>
                  <span className="font-heading font-extrabold text-3xl text-[#0D5C75]/15">{i + 1}</span>
                </div>
                <h3 className="font-heading font-bold text-[#101828] mt-4">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Value proposition */}
        <motion.section {...fade} transition={{ duration: 0.5 }} data-testid="about-value">
          <span className="section-overline mb-3" />
          <h2 className="font-heading text-3xl font-extrabold text-[#101828]">Why Patients Choose SarvaSeva</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {values.map((v) => (
              <div key={v.title} className="card-modern p-7 h-full" data-testid="about-value-card">
                <span className="h-12 w-12 rounded-2xl grid place-items-center bg-[#0D5C75] text-white"><v.icon size={24} /></span>
                <h3 className="font-heading font-bold text-lg text-[#101828] mt-5">{v.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Contact */}
        <motion.section {...fade} transition={{ duration: 0.5 }} data-testid="about-contact"
          className="tricolor-top relative overflow-hidden rounded-3xl mv-gradient-hero text-white p-10 lg:p-14 text-center">
          <Mail size={30} className="mx-auto text-white/90" />
          <h2 className="font-heading text-3xl font-extrabold mt-4">Contact Us</h2>
          <p className="mt-3 text-white/85 max-w-xl mx-auto">
            Have a question about treatment options, pricing or getting started? Our patient care team is here to help.
          </p>
          <a href="mailto:info@sarvaseva.com" data-testid="about-contact-email"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-[#0D5C75] font-semibold h-12 px-7 hover:bg-white/90 transition-colors">
            <Mail size={18} /> info@sarvaseva.com
          </a>
          <div className="mt-6">
            <Link to="/assistant">
              <Button variant="outline" data-testid="about-cta-assistant"
                className="rounded-full h-11 px-6 bg-transparent border-white/50 text-white hover:bg-white/10 hover:text-white">
                Find my hospital <ArrowRight size={17} className="ml-1.5" />
              </Button>
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
